import { useQuery } from '@apollo/client';
import { Close, Refresh } from '@mui/icons-material';
import { Box, CircularProgress, Divider, IconButton, Typography } from '@mui/material';
import type { GetExecutionRunGroups, GetExecutionRunGroupsVariables } from 'types/graphql';
import { useAiChatStore } from '../../store/aiChat.store';
import type { ExecutionRunGroup } from './executionRunGroup.types';
import { UserMessageGroup } from './UserMessageGroup';

const GET_EXECUTION_RUN_GROUPS = gql`
  query GetExecutionRunGroups($conversationId: Int!) {
    aiExecutionRunGroupsForConversation(conversationId: $conversationId) {
      triggerMessage {
        id
        content
      }
      runs {
        id
        runType
        agentType
        triggerContext
        model
        systemPrompt
        inputTokens
        outputTokens
        durationMs
        createdAt
        messages {
          id
          content
          type
          metadata
        }
      }
      totalInputTokens
      totalOutputTokens
    }
  }
`;

export default function AiChatDebugPanel() {
  const conversationId = useAiChatStore((state) => state.conversationId);
  const toggleDebugMode = useAiChatStore((state) => state.toggleDebugMode);

  const { data, loading, refetch } = useQuery<GetExecutionRunGroups, GetExecutionRunGroupsVariables>(
    GET_EXECUTION_RUN_GROUPS,
    { variables: { conversationId: conversationId ?? 0 }, skip: !conversationId }
  );

  const groups: ExecutionRunGroup[] = data?.aiExecutionRunGroupsForConversation ?? [];
  const totalRuns = groups.reduce((sum, g) => sum + g.runs.length, 0);
  const totalTokensIn = groups.reduce((sum, g) => sum + g.totalInputTokens, 0);
  const totalTokensOut = groups.reduce((sum, g) => sum + g.totalOutputTokens, 0);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        flexGrow: 1,
        overflow: 'hidden',
        bgcolor: 'grey.100',
        boxShadow: 'inset 0px 2px 8px rgba(0, 0, 0, 0.08)',
        mt: '12px',
        pt: '12px',
      }}
    >
      {/* Header */}
      <Box sx={{ px: '24px' }}>
        <Box
          sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, justifyContent: 'space-between' }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 500, flex: 1 }}>Debug View</Typography>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {groups.length} messages · {totalRuns} runs · {totalTokensIn} in / {totalTokensOut} out
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {loading ? (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            ) : (
              <IconButton size="small" onClick={() => refetch()}>
                <Refresh fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={toggleDebugMode}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Divider />
      </Box>

      {/* Content */}
      <Box sx={{ p: 1.5, overflow: 'auto', flexGrow: 1, px: '24px' }}>
        {!loading && groups.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No execution runs yet. Send a message to see LLM executions.
          </Typography>
        )}

        {groups.map((group, idx) => (
          <UserMessageGroup
            key={group.triggerMessage.id}
            group={group}
            defaultExpanded={idx === groups.length - 1} // Expand most recent
          />
        ))}
      </Box>
    </Box>
  );
}
